import { useMemo } from 'react';

/**
 * Anti-spam companion for public form submissions.
 *
 * Usage:
 *   const { fields, honeypotInput } = useAntiSpam();
 *   ...
 *   submitEnquiry({ ...formValues, ...fields });
 *   {honeypotInput}    // renders the hidden trap input
 *
 * The hook returns a fresh `form_opened_at` on every mount so that quick
 * copy-paste submissions from bots (fill+submit in < 2s) are caught server-side.
 */
export const useAntiSpam = () => {
  // capture once per mount
  const openedAt = useMemo(() => Date.now(), []);

  const fields = {
    website: '',           // honeypot value from the hidden field lives in the form state
    form_opened_at: openedAt,
  };

  const honeypotInput = (
    <input
      type="text"
      name="website"
      tabIndex={-1}
      autoComplete="off"
      aria-hidden="true"
      style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
      onChange={(e) => {
        // We don't wire this into React state — the server just checks if any
        // value is present in the field named "website". Bots that autofill
        // every input will populate it. Real users can't reach it (offscreen).
        // The parent form reads this DOM value at submit time.
        e.target.setAttribute('data-hp-value', e.target.value);
      }}
    />
  );

  /** Read the honeypot's current value at submit time. */
  const readHoneypot = () => {
    const el = document.querySelector('input[name="website"][aria-hidden="true"]');
    return el ? (el.value || el.getAttribute('data-hp-value') || '') : '';
  };

  return { fields, honeypotInput, readHoneypot, openedAt };
};

export default useAntiSpam;
